import React, { useState, useEffect } from 'react'
import { motion } from 'framer-motion'

const InsightsTab = ({ user, userProfile }) => {
  const [insights, setInsights] = useState([])
  const [loading, setLoading] = useState(true)
  const [selectedTimeframe, setSelectedTimeframe] = useState('month') // week, month, quarter

  useEffect(() => {
    // Generate demo insights
    setInsights(getDemoInsights())
    setLoading(false)
  }, [user, selectedTimeframe])

  const getDemoInsights = () => [
    {
      id: 'demo-1',
      insight_type: 'energy_pattern',
      title: 'Energy Pattern Detected',
      description: 'Based on typical cycle patterns, your energy peaks around day 12. This is when you should schedule your most important work.',
      data: { confidence: 0.75 },
      created_at: new Date().toISOString()
    },
    {
      id: 'demo-2',
      insight_type: 'productivity_trend',
      title: 'Productivity Opportunity',
      description: 'Women typically see 35% higher focus during their follicular phase. Plan deep work sessions for days 6-13.',
      data: { productivity_increase: 35 },
      created_at: new Date().toISOString()
    },
    {
      id: 'demo-3',
      insight_type: 'mood_pattern',
      title: 'Mood Awareness',
      description: 'Your natural rhythm suggests planning social activities during ovulation (days 14-16) when confidence is highest.',
      data: { best_social_days: [14, 15, 16] },
      created_at: new Date().toISOString()
    }
  ]

  const getInsightIcon = (type) => {
    const icons = {
      energy_pattern: '⚡',
      productivity_trend: '📈',
      mood_pattern: '😊',
      cycle_insight: '🔄',
      moon_insight: '🌙'
    }
    return icons[type] || '💡'
  }

  const getInsightColor = (type) => {
    const colors = {
      energy_pattern: 'bg-yellow-100 text-yellow-800',
      productivity_trend: 'bg-green-100 text-green-800',
      mood_pattern: 'bg-blue-100 text-blue-800',
      cycle_insight: 'bg-purple-100 text-purple-800',
      moon_insight: 'bg-indigo-100 text-indigo-800'
    }
    return colors[type] || 'bg-gray-100 text-gray-800'
  }

  const timeframes = [
    { id: 'week', label: 'This Week' },
    { id: 'month', label: 'This Month' },
    { id: 'quarter', label: '3 Months' }
  ]

  const stats = [
    { label: 'Cycles Tracked', value: user ? '3' : 'Demo', icon: '📊' },
    { label: 'Patterns Found', value: insights.length, icon: '🔍' },
    { label: 'Accuracy Score', value: '87%', icon: '🎯' },
    { label: 'Energy Trend', value: '+12%', icon: '⬆️' }
  ]

  if (loading) {
    return (
      <div className="text-center py-8">
        <div className="w-8 h-8 border-2 border-gold border-t-transparent rounded-full animate-spin mx-auto mb-4"></div>
        <p className="text-gray-600">Analyzing your patterns...</p>
      </div>
    )
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="text-center">
        <h2 className="italiana-font text-2xl text-black mb-2">
          Your Personal Insights
        </h2>
        <p className="text-sm text-gray-600">
          {user ? 'Personalized patterns based on your data' : 'Sample insights - create an account for personalized analysis'}
        </p>
      </div>

      {/* Timeframe Selection */}
      <div className="flex justify-center">
        <div className="flex bg-gray-100 rounded-lg p-1">
          {timeframes.map((timeframe) => (
            <button
              key={timeframe.id}
              onClick={() => setSelectedTimeframe(timeframe.id)}
              className={`px-4 py-2 text-sm rounded-md transition-colors ${
                selectedTimeframe === timeframe.id
                  ? 'bg-white text-black shadow-sm'
                  : 'text-gray-600 hover:text-black'
              }`}
            >
              {timeframe.label}
            </button>
          ))}
        </div>
      </div>

      {/* Stats Grid */}
      <div className="grid grid-cols-2 gap-4">
        {stats.map((stat, index) => (
          <motion.div
            key={index}
            initial={{ opacity: 0, y: 10 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: index * 0.1 }}
            className="bg-gray-50 p-4 rounded-lg text-center"
          >
            <div className="text-2xl mb-2">{stat.icon}</div>
            <div className="text-lg font-semibold text-black">{stat.value}</div>
            <div className="text-xs text-gray-600">{stat.label}</div>
          </motion.div>
        ))}
      </div>

      {/* Insights List */}
      <div className="space-y-4">
        <h3 className="font-semibold text-black">Key Insights</h3>
        
        {insights.map((insight, index) => (
          <motion.div
            key={insight.id}
            initial={{ opacity: 0, x: -10 }}
            animate={{ opacity: 1, x: 0 }}
            transition={{ delay: index * 0.1 }}
            className="bg-white border border-gray-200 rounded-lg p-4"
          >
            <div className="flex items-start space-x-3">
              <div className={`p-2 rounded-full ${getInsightColor(insight.insight_type)}`}>
                <span className="text-sm">{getInsightIcon(insight.insight_type)}</span>
              </div>
              <div className="flex-1">
                <h4 className="font-semibold text-black mb-2">{insight.title}</h4>
                <p className="text-sm text-gray-700 leading-relaxed mb-3">
                  {insight.description}
                </p>
                <div className="flex items-center justify-between">
                  <span className={`text-xs px-2 py-1 rounded-full ${getInsightColor(insight.insight_type)}`}>
                    {insight.insight_type.replace('_', ' ').replace(/\b\w/g, l => l.toUpperCase())}
                  </span>
                  <span className="text-xs text-gray-500">
                    {new Date(insight.created_at).toLocaleDateString()}
                  </span>
                </div>
              </div>
            </div>
          </motion.div>
        ))}
      </div>

      {/* Insights Actions */}
      <div className="bg-gray-50 p-4 rounded-lg">
        <h4 className="font-medium text-black mb-3">Improve Your Insights</h4>
        <div className="space-y-2 text-sm">
          <div className="flex items-center space-x-2 text-gray-700">
            <span>📝</span>
            <span>Track daily energy levels for better predictions</span>
          </div>
          <div className="flex items-center space-x-2 text-gray-700">
            <span>📅</span>
            <span>Log activities to identify optimal timing</span>
          </div>
          <div className="flex items-center space-x-2 text-gray-700">
            <span>💭</span>
            <span>Add mood notes for comprehensive analysis</span>
          </div>
        </div>
        
        <button className="w-full mt-4 bg-white border border-gray-300 text-gray-700 py-2 rounded-lg text-sm hover:bg-gray-100 transition-colors">
          Start Tracking More Data
        </button>
      </div>

      {/* Coming Soon Notice */}
      <div className="bg-blue-50 p-4 rounded-lg text-center">
        <h4 className="font-medium text-blue-900 mb-2">🚀 Advanced Insights Coming Soon</h4>
        <p className="text-sm text-blue-800">
          AI-powered pattern recognition, predictive analytics, and personalized recommendations 
          based on your unique cycle data and lifestyle patterns.
        </p>
      </div>
    </div>
  )
}

export default InsightsTab